import { useWebSocket } from '@vueuse/core';
import { useAuthStore } from '../stores/auth.store';
import Config from '../utils/config';

import _ from 'lodash';

class WebSocketService {
    #socket = null;
    #topics = {};
    #subscribersQueue = [];
    #messagesQueue = [];

    #allMessagesHistory = [];
    #activeSubscriptions = new Set();

    constructor() {}

    /**
     * Safely stringifies a value for use in keys, handling null/undefined/empty values
     * @param {any} value
     * @returns {string}
     */
    #safeStringify(value) {
        if (_.isNil(value)) {
            return 'null';
        }
        if (_.isArray(value) || _.isObject(value)) {
            if (_.isEmpty(value)) {
                return _.isArray(value) ? '[]' : '{}';
            }
            try {
                return JSON.stringify(value);
            } catch (error) {
                console.warn('WebSocket: Failed to stringify value:', value, error);
                return _.isArray(value) ? '[]' : '{}';
            }
        }
        return String(value);
    }

    /**
     * Initializes the WebSocket connection with authentication and event handlers.
     * Clears any existing connections and sets up auto-reconnection logic.
     *
     * @returns {void}
     */
    init() {
        if (Config.app.disableWebSocket) {
            console.log('WebSocket: disabled');
            return;
        }
        this.close();

        // Clear queues and history when reinitializing (e.g., on auth token change)
        this.#subscribersQueue = [];
        this.#messagesQueue = [];
        this.#allMessagesHistory = [];
        this.#topics = {};
        this.#activeSubscriptions.clear();

        const authStore = useAuthStore();
        const authProtocol = `Authorization#${authStore.accessToken}`;

        this.#socket = useWebSocket(`${Config.app.wsUrl}`, {
            autoReconnect: {
                retries: 10,
                delay: 5000,
                onFailed: () => {
                    console.log('WebSocket: Failed to connect after 10 retries');
                    //  SentryService.notify(new Error('WebSocket: Failed to connect after 10 retries'), { context: 'websockets', severity: 'error' });
                }
            },
            protocols: [authProtocol],
            onConnected: () => {
                console.log('WebSocket: connected!');

                // If we have queued subscribers, send them and skip historical processing
                // (the queued subscribers represent the current state)
                if (this.#subscribersQueue?.length > 0) {
                    this.processSubscribersQueue();
                } else {
                    // No queued subscribers, so this is a reconnect - send historical subscriptions
                    this.processHistoricalMessagesQueue();
                }

                // Always process regular message queue if it has messages
                if (this.#messagesQueue?.length > 0) {
                    this.processMessagesQueue();
                }
            },
            onDisconnected: () => console.log('WebSocket: disconnected!'),
            onError: (_ws, event) => {
                console.error('WebSocket: connection error!', event);
                // SentryService.notify(new Error('WebSocket connection error'), {
                //     context: 'websocket-connection',
                //     severity: 'error',
                //     extra: { event }
                // });
            },
            onMessage: (_ws, message) => {
                try {
                    this.onMessage(JSON.parse(message.data));
                } catch (parseError) {
                    console.error('WebSocket: Failed to parse message:', parseError, message.data);
                    // SentryService.notify(parseError, {
                    //     context: 'websocket-parse',
                    //     severity: 'error',
                    //     extra: { rawMessage: message.data }
                    // });
                }
            }
        });
    }

    onMessage(message) {
        if (Config.app.disableWebSocket) {
            console.log('WebSocket: disabled');
            return;
        }

        try {
            if (this.#topics[message.type]) {
                this.#topics[message.type].forEach((listenerInfo) => {
                    try {
                        const fromWebSocket = true;
                        listenerInfo.callback(message.data, {}, fromWebSocket);
                    } catch (callbackError) {
                        console.error('WebSocket: Error in listener callback:', callbackError, {
                            topic: message.type,
                            callbackId: listenerInfo.callbackId
                        });
                        // SentryService.notify(callbackError, {
                        //     context: 'websocket-callback',
                        //     severity: 'error',
                        //     extra: { topic: message.type, callbackId: listenerInfo.callbackId }
                        // });
                    }
                });
            }
        } catch (error) {
            console.error('WebSocket: Error processing message:', error, message);
            // SentryService.notify(error, { context: 'websocket-message-processing', severity: 'error' });
        }
    }

    open() {
        if (Config.app.disableWebSocket) {
            console.log('WebSocket: disabled');
            return;
        }

        if (this.#socket && this.#socket.status.value === 'CLOSED') {
            this.#socket.open();
        }
    }

    close() {
        if (Config.app.disableWebSocket) {
            console.log('WebSocket: disabled');
            return;
        }

        if (this.#socket && this.#socket.status.value !== 'CLOSED') {
            this.#socket.close();
        }
    }

    getStatus() {
        if (Config.app.disableWebSocket) {
            return 'DISABLED';
        }
        return this.#socket?.status?.value || 'NOT_INITIALIZED';
    }

    isConnected() {
        return this.getStatus() === 'OPEN';
    }

    /**
     * Adds a WebSocket listener for the specified topic with configuration options.
     *
     * @param {Object} config - Configuration object for the listener
     * @param {string} config.topic - The topic to subscribe to (e.g., 'metrics', 'events')
     * @param {Function} config.callback - The callback function to handle incoming messages
     * @param {string} config.callbackId - Unique identifier for the callback (for removal)
     * @param {string|null} [config.entityType=null] - The entity type filter (e.g., 'sources', 'destinations')
     * @param {string|null} [config.entityId=null] - The specific entity ID to filter by
     * @param {number|null} [config.startListFrom=null] - Starting offset for message list
     * @param {number|null} [config.listSize=null] - Number of messages to retrieve
     * @param {string|null} [config.timeType=null] - Time-based filtering type (e.g., 'hourly', 'daily')
     * @param {string[]|null} [config.entityLevels=null] - Array of entity level filters (e.g., ['topic', 'partition'])
     *
     * @example
     * WebSocketService.addListener({
     *   topic: 'metrics',
     *   entityType: 'sources',
     *   entityId: 'source-123',
     *   callback: handleMetrics,
     *   callbackId: 'metrics.onMetricsData',
     *   startListFrom: 0,
     *   listSize: 10,
     *   timeType: 'hourly',
     *   entityLevels: ['topic', 'partition']
     * });
     *
     * @returns {void}
     */
    addListener({
        topic,
        callback,
        callbackId,
        entityType = null,
        entityId = null,
        startListFrom = null,
        listSize = null,
        timeType = null,
        entityLevels = null
    } = {}) {
        if (Config.app.disableWebSocket) {
            console.log('WebSocket: disabled');
            return;
        }

        if (!callback || !callbackId || !topic) {
            console.error('WebSocket: topic, callback and callbackId are required');
            return;
        }

        // Normalize undefined/null values to ensure consistent matching
        // Use nullish coalescing (??) for numbers to preserve valid 0 values
        const normalizedEntityType = entityType || null;
        const normalizedEntityId = entityId || null;
        const normalizedStartListFrom = startListFrom ?? null;
        const normalizedListSize = listSize ?? null;
        const normalizedTimeType = timeType || null;
        const normalizedEntityLevels = entityLevels || null;

        if (import.meta.env.VITE_ENVIRONMENT === 'development' || import.meta.env.NODE_ENV === 'development') {
            console.log('WebSocket: adding listener..', topic);
        }

        // Check if we already have an active subscription for this exact combination
        if (
            this.hasActiveSubscription({
                topic,
                entityType: normalizedEntityType,
                entityId: normalizedEntityId,
                startListFrom: normalizedStartListFrom,
                listSize: normalizedListSize,
                timeType: normalizedTimeType,
                entityLevels: normalizedEntityLevels
            })
        ) {
            if (import.meta.env.VITE_ENVIRONMENT === 'development' || import.meta.env.NODE_ENV === 'development') {
                console.warn('WebSocket: subscription already active, skipping..', {
                    topic,
                    entityType: normalizedEntityType,
                    entityId: normalizedEntityId
                });
            }
            // Still add to local topics tracking for callbacks, but don't send subscription message
            const listenerInfo = {
                callback,
                callbackId,
                entityType: normalizedEntityType,
                entityId: normalizedEntityId,
                startListFrom: normalizedStartListFrom,
                listSize: normalizedListSize,
                timeType: normalizedTimeType,
                entityLevels: normalizedEntityLevels
            };

            if (!this.#topics[topic]) {
                this.#topics[topic] = [listenerInfo];
            } else {
                const isDuplicate = this.#topics[topic].some(
                    (item) =>
                        item.callbackId === callbackId &&
                        item.entityType === normalizedEntityType &&
                        item.entityId === normalizedEntityId &&
                        item.startListFrom === normalizedStartListFrom &&
                        item.listSize === normalizedListSize &&
                        item.timeType === normalizedTimeType &&
                        _.isEqual(item.entityLevels, normalizedEntityLevels)
                );

                if (!isDuplicate) {
                    this.#topics[topic].push(listenerInfo);
                }
            }
            return;
        }

        let listenerInfo = {
            callback,
            callbackId,
            entityType: normalizedEntityType,
            entityId: normalizedEntityId,
            startListFrom: normalizedStartListFrom,
            listSize: normalizedListSize,
            timeType: normalizedTimeType,
            entityLevels: normalizedEntityLevels
        };

        if (!this.#topics[topic]) {
            this.#topics[topic] = [listenerInfo];
        } else {
            const isDuplicate = this.#topics[topic].some(
                (item) =>
                    item.callbackId === callbackId &&
                    item.entityType === normalizedEntityType &&
                    item.entityId === normalizedEntityId &&
                    item.startListFrom === normalizedStartListFrom &&
                    item.listSize === normalizedListSize &&
                    item.timeType === normalizedTimeType &&
                    _.isEqual(item.entityLevels, normalizedEntityLevels)
            );

            if (!isDuplicate) {
                this.#topics[topic].push(listenerInfo);
            } else {
                if (import.meta.env.VITE_ENVIRONMENT === 'development' || import.meta.env.NODE_ENV === 'development') {
                    console.warn('WebSocket: duplicate listener detected, skipping..', { topic, callbackId });
                }
                return;
            }
        }

        this.sendListenerMessage(topic, listenerInfo, 'subscribe');
    }

    /**
     * Removes all active WebSocket listeners and clears subscription history.
     * Sends unsubscribe messages to the server for all active subscriptions.
     *
     * @returns {void}
     */
    removeAllListeners() {
        if (Config.app.disableWebSocket) {
            console.log('WebSocket: disabled');
            return;
        }
        if (import.meta.env.VITE_ENVIRONMENT === 'development' || import.meta.env.NODE_ENV === 'development') {
            console.log('WebSocket: removing all listeners');
        }

        for (const topic in this.#topics) {
            const listenersInfo = this.#topics[topic];

            if (listenersInfo && listenersInfo.length > 0) {
                listenersInfo.forEach((listenerInfo) => {
                    this.sendListenerMessage(topic, listenerInfo, 'unsubscribe');
                });
            }
        }

        // Clear all subscription messages from history when removing all listeners
        this.#allMessagesHistory = this.#allMessagesHistory.filter((msg) => msg.message.action !== 'subscribe');
        this.#topics = {};
        this.#activeSubscriptions.clear();
    }

    /**
     * Removes a WebSocket listener for the specified topic with configuration options.
     *
     * @param {Object} config - Configuration object for the listener to remove
     * @param {string} config.topic - The topic to unsubscribe from (e.g., 'metrics', 'events')
     * @param {string} config.callbackId - Unique identifier for the callback to remove
     * @param {string|null} [config.entityType=null] - The entity type filter (e.g., 'sources', 'destinations')
     * @param {string|null} [config.entityId=null] - The specific entity ID to filter by
     * @param {number|null} [config.startListFrom=null] - Starting offset for message list
     * @param {number|null} [config.listSize=null] - Number of messages to retrieve
     * @param {string|null} [config.timeType=null] - Time-based filtering type (e.g., 'hourly', 'daily')
     * @param {string[]|null} [config.entityLevels=null] - Array of entity level filters (e.g., ['topic', 'partition'])
     *
     * @example
     * WebSocketService.removeListener({
     *   topic: 'metrics',
     *   entityType: 'sources',
     *   entityId: 'source-123',
     *   callbackId: 'metrics.onMetricsData',
     *   startListFrom: 0,
     *   listSize: 10,
     *   timeType: 'hourly',
     *   entityLevels: ['topic', 'partition']
     * });
     *
     * @returns {void}
     */
    removeListener({
        topic,
        callbackId,
        entityType = null,
        entityId = null,
        startListFrom = null,
        listSize = null,
        timeType = null,
        entityLevels = null
    } = {}) {
        if (Config.app.disableWebSocket) {
            console.log('WebSocket: disabled');
            return;
        }

        if (!topic || !callbackId) {
            console.error('WebSocket: topic and callbackId are required');
            return;
        }

        if (import.meta.env.VITE_ENVIRONMENT === 'development' || import.meta.env.NODE_ENV === 'development') {
            console.log('WebSocket: removing listener..', topic);
        }

        // Normalize undefined/null values to ensure consistent matching with addListener
        // Use nullish coalescing (??) for numbers to preserve valid 0 values
        const normalizedEntityType = entityType || null;
        const normalizedEntityId = entityId || null;
        const normalizedStartListFrom = startListFrom ?? null;
        const normalizedListSize = listSize ?? null;
        const normalizedTimeType = timeType || null;
        const normalizedEntityLevels = entityLevels || null;

        if (this.#topics[topic]) {
            // Find the exact listener to remove
            const removedListeners = [];
            this.#topics[topic] = this.#topics[topic].filter((item) => {
                const shouldRemove =
                    item.callbackId === callbackId &&
                    item.entityType === normalizedEntityType &&
                    item.entityId === normalizedEntityId &&
                    item.startListFrom === normalizedStartListFrom &&
                    item.listSize === normalizedListSize &&
                    item.timeType === normalizedTimeType &&
                    _.isEqual(item.entityLevels, normalizedEntityLevels);

                if (shouldRemove) {
                    removedListeners.push(item);
                }
                return !shouldRemove;
            });

            // Send unsubscribe message for each removed listener
            removedListeners.forEach((listener) => {
                this.sendListenerMessage(topic, listener, 'unsubscribe');
            });

            // Clean up topic if no listeners remain
            if (this.#topics[topic].length === 0) {
                delete this.#topics[topic];
            }
        }
    }

    /**
     * Sends a generic message through the WebSocket connection.
     * Queues the message if the connection is not ready.
     *
     * @param {string} topic - The message topic/type
     * @param {Object} message - The message payload
     * @returns {void}
     */
    sendMessage(topic, message) {
        if (Config.app.disableWebSocket) {
            console.log('WebSocket: disabled');
            return;
        }
        if (this.#socket && this.#socket.send && this.#socket.status.value === 'OPEN') {
            const cleanedMessage = _.omitBy(message || {}, _.isNil);
            const payload = JSON.stringify({ type: topic, ...cleanedMessage });
            if (import.meta.env.VITE_ENVIRONMENT === 'development' || import.meta.env.NODE_ENV === 'development') {
                console.log('WebSocket: sending message..', payload);
            }
            this.#socket.send(payload);
        } else {
            if (import.meta.env.VITE_ENVIRONMENT === 'development' || import.meta.env.NODE_ENV === 'development') {
                console.log('WebSocket: adding message to the queue (websocket not ready yet)..', { topic, message });
            }
            this.#messagesQueue.push({ topic, message });
        }
    }

    processSubscribersQueue() {
        if (Config.app.disableWebSocket) {
            console.log('WebSocket: disabled');
            return;
        }
        if (this.#subscribersQueue && this.#subscribersQueue.length > 0) {
            if (import.meta.env.VITE_ENVIRONMENT === 'development' || import.meta.env.NODE_ENV === 'development') {
                console.log('WebSocket: processing all messages in the SubscribersQueue..');
            }

            for (const subMsg of this.#subscribersQueue) {
                // Send directly to avoid double queuing
                if (this.#socket && this.#socket.send && this.#socket.status.value === 'OPEN') {
                    const message = {
                        action: subMsg.action,
                        entity_type: subMsg.entity_type,
                        entity_id: subMsg.entity_id,
                        topic_list_from: subMsg.topic_list_from,
                        topic_list_size: subMsg.topic_list_size,
                        time_type: subMsg.time_type,
                        entity_levels: subMsg.entity_levels
                    };
                    const cleanedMessage = _.omitBy(message || {}, _.isNil);
                    const payload = JSON.stringify({ type: subMsg.topic, ...cleanedMessage });
                    if (import.meta.env.VITE_ENVIRONMENT === 'development' || import.meta.env.NODE_ENV === 'development') {
                        console.log('WebSocket: sending queued subscriber message..', payload);
                    }
                    this.#socket.send(payload);

                    // Track active subscriptions for queued messages too
                    if (subMsg.action === 'subscribe') {
                        const subscriptionKey = this.#generateSubscriptionKey(subMsg.topic, message);
                        this.#activeSubscriptions.add(subscriptionKey);
                    }
                }
            }

            this.#subscribersQueue = [];
        }
    }

    processMessagesQueue() {
        if (Config.app.disableWebSocket) {
            console.log('WebSocket: disabled');
            return;
        }
        if (this.#messagesQueue && this.#messagesQueue.length > 0) {
            if (import.meta.env.VITE_ENVIRONMENT === 'development' || import.meta.env.NODE_ENV === 'development') {
                console.log('WebSocket: processing all messages in the MessagesQueue..');
            }

            for (const msg of this.#messagesQueue) {
                // Send directly to avoid double queuing
                if (this.#socket && this.#socket.send && this.#socket.status.value === 'OPEN') {
                    const cleanedMessage = _.omitBy(msg.message || {}, _.isNil);
                    const payload = JSON.stringify({ type: msg.topic, ...cleanedMessage });
                    if (import.meta.env.VITE_ENVIRONMENT === 'development' || import.meta.env.NODE_ENV === 'development') {
                        console.log('WebSocket: sending queued message..', payload);
                    }
                    this.#socket.send(payload);
                }
            }

            this.#messagesQueue = [];
        }
    }

    processHistoricalMessagesQueue() {
        if (Config.app.disableWebSocket) {
            console.log('WebSocket: disabled');
            return;
        }
        // remove message duplicates using a safer key generation
        this.#allMessagesHistory = _.uniqBy(this.#allMessagesHistory, (msg) => {
            const safeMsg = _.omitBy(msg, _.isNil);
            return JSON.stringify(safeMsg);
        });

        if (this.#allMessagesHistory && this.#allMessagesHistory.length > 0) {
            if (import.meta.env.VITE_ENVIRONMENT === 'development' || import.meta.env.NODE_ENV === 'development') {
                console.log('WebSocket (post automatic reconnect): processing all messages historically in the allMessagesHistory..');
            }

            // Clear active subscriptions on reconnect to allow re-subscription
            this.#activeSubscriptions.clear();

            // Only re-send subscription messages that aren't already active
            const subscriptionMessages = this.#allMessagesHistory.filter((msg) => msg.message.action === 'subscribe');

            for (const msg of subscriptionMessages) {
                const subscriptionKey = this.#generateSubscriptionKey(msg.topic, msg.message);

                // Only re-subscribe if we haven't already sent this subscription after reconnect
                if (!this.#activeSubscriptions.has(subscriptionKey)) {
                    if (this.#socket && this.#socket.send && this.#socket.status.value === 'OPEN') {
                        const cleanedMessage = _.omitBy(msg.message || {}, _.isNil);
                        const payload = JSON.stringify({ type: msg.topic, ...cleanedMessage });
                        if (import.meta.env.VITE_ENVIRONMENT === 'development' || import.meta.env.NODE_ENV === 'development') {
                            console.log('WebSocket: sending historical subscription message..', payload);
                        }
                        this.#socket.send(payload);
                        this.#activeSubscriptions.add(subscriptionKey);
                    }
                }
            }

            // Send non-subscription messages
            const nonSubscriptionMessages = this.#allMessagesHistory.filter((msg) => msg.message.action !== 'subscribe');
            for (const msg of nonSubscriptionMessages) {
                if (this.#socket && this.#socket.send && this.#socket.status.value === 'OPEN') {
                    const cleanedMessage = _.omitBy(msg.message || {}, _.isNil);
                    const payload = JSON.stringify({ type: msg.topic, ...cleanedMessage });
                    if (import.meta.env.VITE_ENVIRONMENT === 'development' || import.meta.env.NODE_ENV === 'development') {
                        console.log('WebSocket: sending historical message..', payload);
                    }
                    this.#socket.send(payload);
                }
            }
        }
    }

    sendListenerMessage(topic, listenerInfo, action) {
        if (Config.app.disableWebSocket) {
            console.log('WebSocket: disabled');
            return;
        }
        // Build message object with only non-null/undefined properties
        const message = { action };

        // For strings, check both nil and empty
        if (!_.isNil(listenerInfo.entityType) && listenerInfo.entityType !== '') {
            message.entity_type = listenerInfo.entityType;
        }
        if (!_.isNil(listenerInfo.entityId) && listenerInfo.entityId !== '') {
            message.entity_id = listenerInfo.entityId;
        }

        // For numbers, only check nil (0 is valid)
        if (!_.isNil(listenerInfo.startListFrom)) {
            message.topic_list_from = listenerInfo.startListFrom;
        }
        if (!_.isNil(listenerInfo.listSize)) {
            message.topic_list_size = listenerInfo.listSize;
        }

        // For strings, check both nil and empty
        if (!_.isNil(listenerInfo.timeType) && listenerInfo.timeType !== '') {
            message.time_type = listenerInfo.timeType;
        }

        // For arrays, check nil and empty array
        if (!_.isNil(listenerInfo.entityLevels) && !_.isEmpty(listenerInfo.entityLevels)) {
            message.entity_levels = listenerInfo.entityLevels;
        }

        const subscriptionKey = this.#generateSubscriptionKey(topic, message);

        if (action === 'subscribe') {
            // Check if we already have this exact subscription in history to prevent duplicates
            const isDuplicateInHistory = this.#allMessagesHistory.some((msg) => {
                return (
                    msg.topic === topic &&
                    msg.message.action === 'subscribe' &&
                    msg.message.entity_type === listenerInfo.entityType &&
                    msg.message.entity_id === listenerInfo.entityId &&
                    msg.message.topic_list_from === listenerInfo.startListFrom &&
                    msg.message.topic_list_size === listenerInfo.listSize &&
                    msg.message.time_type === listenerInfo.timeType &&
                    _.isEqual(msg.message.entity_levels, listenerInfo.entityLevels)
                );
            });

            if (!isDuplicateInHistory) {
                this.#allMessagesHistory.push({ topic, message });
            }
        } else if (action === 'unsubscribe') {
            // Remove matching subscription from history and active tracking when unsubscribing
            this.#allMessagesHistory = this.#allMessagesHistory.filter((msg) => {
                const isMatchingSubscription =
                    msg.topic === topic &&
                    msg.message.action === 'subscribe' &&
                    msg.message.entity_type === listenerInfo.entityType &&
                    msg.message.entity_id === listenerInfo.entityId &&
                    msg.message.topic_list_from === listenerInfo.startListFrom &&
                    msg.message.topic_list_size === listenerInfo.listSize &&
                    msg.message.time_type === listenerInfo.timeType &&
                    _.isEqual(msg.message.entity_levels, listenerInfo.entityLevels);
                return !isMatchingSubscription;
            });
            this.#activeSubscriptions.delete(subscriptionKey);
        }

        // Use a specialized queue for listener messages to ensure they get processed correctly
        if (this.#socket && this.#socket.send && this.#socket.status.value === 'OPEN') {
            const cleanedMessage = _.omitBy(message || {}, _.isNil);
            const payload = JSON.stringify({ type: topic, ...cleanedMessage });
            if (import.meta.env.VITE_ENVIRONMENT === 'development' || import.meta.env.NODE_ENV === 'development') {
                console.log('WebSocket: sending listener message..', payload);
            }

            this.#socket.send(payload);

            // Track active subscriptions to prevent duplicates
            if (action === 'subscribe') {
                this.#activeSubscriptions.add(subscriptionKey);
            }
        } else {
            if (import.meta.env.VITE_ENVIRONMENT === 'development' || import.meta.env.NODE_ENV === 'development') {
                console.log('WebSocket: adding listener message to the queue (websocket not ready yet)..', { topic, message });
            }
            this.#subscribersQueue.push({
                topic,
                action: message.action,
                entity_type: message.entity_type,
                entity_id: message.entity_id,
                topic_list_from: message.topic_list_from,
                topic_list_size: message.topic_list_size,
                time_type: message.time_type,
                entity_levels: message.entity_levels
            });

            // Optimistically track subscription to prevent duplicates before WebSocket connects
            if (action === 'subscribe') {
                this.#activeSubscriptions.add(subscriptionKey);
            }
        }
    }

    #generateSubscriptionKey(topic, message) {
        return `${topic}:${message.entity_type || 'null'}:${message.entity_id || 'null'}:${message.topic_list_from || 'null'}:${message.topic_list_size || 'null'}:${message.time_type || 'null'}:${this.#safeStringify(message.entity_levels)}`;
    }

    /**
     * Checks if there is an active subscription for the given parameters.
     *
     * @param {Object} config - Configuration object to check
     * @param {string} config.topic - The topic to check
     * @param {string|null} [config.entityType=null] - The entity type filter
     * @param {string|null} [config.entityId=null] - The entity ID filter
     * @param {number|null} [config.startListFrom=null] - The pagination offset
     * @param {number|null} [config.listSize=null] - The pagination size
     * @param {string|null} [config.timeType=null] - The time-based filter
     * @param {string[]|null} [config.entityLevels=null] - The entity level filters
     * @returns {boolean} True if an active subscription exists
     */
    hasActiveSubscription({
        topic,
        entityType = null,
        entityId = null,
        startListFrom = null,
        listSize = null,
        timeType = null,
        entityLevels = null
    } = {}) {
        const message = {
            entity_type: entityType,
            entity_id: entityId,
            topic_list_from: startListFrom,
            topic_list_size: listSize,
            time_type: timeType,
            entity_levels: entityLevels
        };
        const subscriptionKey = this.#generateSubscriptionKey(topic, message);
        return this.#activeSubscriptions.has(subscriptionKey);
    }
}

export default new WebSocketService();
